/**
 * One turn, from POST to the last frame — including everything that can go
 * wrong in between.
 *
 * The recovery ladder (§3.5), in the order it is climbed down:
 *   1. the stream breaks       → `GET …/runs/{rid}/stream` with `Last-Event-ID`,
 *                                3 tries, 2/4/8 s. Nothing is lost or repeated.
 *   2. the stream ends cleanly → normal completion (there is no `end` frame on
 *                                this build), but if the run is still `running`
 *                                the close was a silent truncation and we resume.
 *   3. events are gone (>1 h)  → `GET …/state`, which lives in the checkpoint.
 *   4. the run is terminal     → its status is the verdict; «Повторить», not a
 *                                spinner that never stops.
 *
 * `on_disconnect: "continue"` is sent EXPLICITLY: the default is `"cancel"`, and
 * with it every one of those rungs would be recovering a run the first broken
 * TCP connection had already killed.
 */

import {
  ApiError,
  cancelRun,
  createThread,
  getRun,
  resumeRun,
  startRun,
  type RunContext,
} from './api';
import { normalizeFrame, type NormalizedEvent } from './events';
import { readSse, StreamingUnsupportedError } from './sse';
import { observeFrame, recordDiagnostic } from '../devtools/diagnostics';

export type TurnHandlers = {
  onEvent: (event: NormalizedEvent) => void;
  /** Fired when a break is being recovered from, and again when it is over. */
  onReconnect?: (attempt: number, of: number) => void;
  onRecovered?: () => void;
};

export type TurnRequest = {
  threadId: string;
  assistantId: string;
  /**
   * The visitor's own words — or an empty string for `peterhof_live`'s REPORT
   * run (`App.tsx`'s `scheduleReport`), which carries no `HumanMessage` at all
   * (`context.live_report` names the job instead), or for a `command`
   * (below), which needs no `HumanMessage` either. An empty string is read
   * below as «send no message», never as an empty one.
   */
  text: string;
  /**
   * Answers a pending questionnaire instead of starting an ordinary turn
   * (`App.tsx`'s `submitResume`) — see `StartRunBody.command`. When set,
   * `text` is ignored and no `input` is sent at all.
   */
  command?: { resume: unknown };
  context: RunContext;
  streamMode: string[];
  handlers: TurnHandlers;
  signal: AbortSignal;
};

export type TurnOutcome =
  | { status: 'done' }
  | { status: 'cancelled' }
  | { status: 'error'; error: ApiError | Error }
  | { status: 'unsupported' };

const RESUME_DELAYS_MS = [2000, 4000, 8000];

const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('aborted', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });

const isAbort = (error: unknown): boolean =>
  error instanceof DOMException ? error.name === 'AbortError' : (error as Error)?.name === 'AbortError';

/**
 * Runs one turn to completion.
 *
 * The caller owns the `AbortController`: aborting it is «Стоп», which stops the
 * rendering. Telling the SERVER to stop is `cancelTurn` below, and the two are
 * deliberately separate — a closed tab must not kill a run, a pressed button must.
 */
export async function runTurn(request: TurnRequest): Promise<TurnOutcome> {
  const { threadId, assistantId, text, command, context, streamMode, handlers, signal } = request;
  let runId: string | null = null;
  let lastEventId: string | null = null;
  let sawTerminalFrame = false;

  recordDiagnostic({ threadId, assistantId }, 'events', 'Запрос', { assistantId, context, streamMode, resumed: !!command });
  const consume = async (response: Response): Promise<void> => {
    for await (const frame of readSse(response, { signal })) {
      if (frame.id) lastEventId = frame.id;
      observeFrame(frame, { threadId, runId, assistantId });
      const event = normalizeFrame(frame);
      if (event.kind === 'run_started') runId = event.runId;
      if (event.kind === 'run_end' || event.kind === 'run_error') sawTerminalFrame = true;
      if (event.kind !== 'ignored') handlers.onEvent(event);
    }
  };

  try {
    // Best effort, and deliberately not awaited for its VERDICT: reagent
    // creates the thread implicitly on the first run, so this call is
    // bookkeeping rather than a precondition — and live on dev a 500 on it
    // (the path was down for four seconds) failed the turn before the run was
    // ever attempted, with a generic «гид не отвечает» in place of whatever the
    // run itself would have said. A cancel still cancels.
    try {
      await createThread(threadId, signal);
    } catch (error) {
      if (isAbort(error) && signal.aborted) return { status: 'cancelled' };
      // eslint-disable-next-line no-console
      console.warn('[run] createThread did not answer; the run creates it', error);
    }
    const response = await startRun(
      threadId,
      {
        assistant_id: assistantId,
        // `command` answers a pending interrupt instead of starting a turn —
        // the two are mutually exclusive, and reagent reads whichever is
        // present. Otherwise: only the new message, since the history lives
        // on the server (UI_CONTRACT §0); `[]` for `peterhof_live`'s report
        // run — `text` is deliberately empty there, and reagent needs no
        // trailing `HumanMessage` to serve it.
        ...(command ? { command } : { input: { messages: text ? [{ role: 'user', content: text }] : [] } }),
        stream_mode: streamMode,
        on_disconnect: 'continue',
        context,
      },
      signal,
    );
    // `Content-Location` names the run before a single frame arrives, which is
    // what makes «Стоп» work during the first seconds of a slow turn.
    runId = readRunId(response) ?? runId;
    await consume(response);
  } catch (error) {
    recordDiagnostic({ threadId, runId, assistantId }, 'events', 'Ошибка / отмена', error instanceof Error ? { name: error.name, message: error.message } : error);
    if (isAbort(error)) return { status: 'cancelled' };
    if (error instanceof StreamingUnsupportedError) return { status: 'unsupported' };
    if (error instanceof ApiError) return { status: 'error', error };
    // A network failure before any frame: nothing to resume from.
    if (!runId) return { status: 'error', error: error as Error };
    const resumed = await resumeLadder();
    return resumed;
  }

  if (sawTerminalFrame) return { status: 'done' };
  // A clean close IS the end of a run on this build — unless the server says the
  // run is still going, in which case the close was a truncation (a deploy
  // recreating the container looks exactly like this).
  return await verifyOrResume();

  function readRunId(response: Response): string | null {
    const location = response.headers.get('Content-Location') || response.headers.get('Location');
    const match = location?.match(/\/runs\/([0-9a-fA-F-]{36})/);
    return match?.[1] ?? null;
  }

  /**
   * The stream ended without a terminal frame. Ask whether the run is still
   * going — and if the path is down, say so instead of pretending.
   *
   * Every attempt carries a deadline (`api.getRun`), so this cannot hang; a
   * transient blip is covered by asking a few times over ~6 s. What it may NOT
   * do is what it did before: swallow the failure and report `done`, which
   * ends the turn over a half-written answer with nothing to retry.
   */
  async function verifyOrResume(): Promise<TurnOutcome> {
    if (!runId) return { status: 'done' };
    let lastError: unknown = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const run = await getRun(threadId, runId, signal);
        if (run.status === 'running' || run.status === 'pending' || run.status === 'queued') {
          return await resumeLadder();
        }
        return { status: 'done' };
      } catch (error) {
        if (isAbort(error) && signal.aborted) return { status: 'cancelled' };
        lastError = error;
        try {
          await sleep(2000, signal);
        } catch {
          return { status: 'cancelled' };
        }
      }
    }
    // eslint-disable-next-line no-console
    console.warn('[run] could not confirm how the run ended', lastError);
    return { status: 'error', error: new Error('stream-lost') };
  }

  async function resumeLadder(): Promise<TurnOutcome> {
    if (!runId) return { status: 'error', error: new Error('stream-lost') };
    for (let attempt = 0; attempt < RESUME_DELAYS_MS.length; attempt += 1) {
      recordDiagnostic({ threadId, runId, assistantId }, 'events', 'Переподключение', { attempt: attempt + 1 });
      handlers.onReconnect?.(attempt + 1, RESUME_DELAYS_MS.length);
      try {
        await sleep(RESUME_DELAYS_MS[attempt] ?? 8000, signal);
        const response = await resumeRun(threadId, runId, lastEventId, signal);
        await consume(response);
        handlers.onRecovered?.();
        if (sawTerminalFrame) return { status: 'done' };
        const run = await getRun(threadId, runId, signal);
        if (run.status !== 'running' && run.status !== 'pending' && run.status !== 'queued') {
          return { status: 'done' };
        }
      } catch (error) {
        if (isAbort(error)) return { status: 'cancelled' };
        // 404 means the event log has been swept (it is kept one hour). The
        // checkpoint outlives it, so the caller falls back to `GET …/state`.
        if (error instanceof ApiError && error.status === 404) {
          return { status: 'error', error };
        }
      }
    }
    return { status: 'error', error: new Error('stream-lost') };
  }
}

/**
 * Server-side cancel. Rendering is stopped by aborting the controller; this is
 * what stops the model. Failure is swallowed on purpose — the visitor pressed
 * «Стоп» and the UI has already obeyed.
 */
export async function cancelTurn(threadId: string, runId: string): Promise<void> {
  try {
    await cancelRun(threadId, runId, 'interrupt');
  } catch {
    /* the run will finish on its own; the visitor is no longer watching it */
  }
}
